import { Canvas } from '@react-three/fiber';
import { CSSProperties, ReactNode, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { PCFSoftShadowMap } from 'three';
import { useSnapshot } from 'valtio';

import Grid from '@cartwhl/3d/r3f/components/Grid';
import GroundPlane from '@cartwhl/3d/r3f/components/GroundPlane';
import { isNonProduction } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/Text';
import useProgressEstimate, {
  GENERATION_ESTIMATED_SECONDS,
} from '@cartwhl/ui/hooks/useProgressEstimate';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import PlayerDialog from '@cartwhl/ui/molecules/PlayerDialog';

import useCanvasResizer from 'hooks/useCanvasResizer';
import {
  animationGenerationStore,
  animationPlayerStore,
  sceneSettingsStore,
} from 'stores';

import usePlayerHotkeys from '../usePlayerHotkeys';
import CanvasError from './CanvasError';
import SceneSetup from './SceneSetup';
import StageFromBVH from './StageFromBVH';

export default function AnimationMainPlayer({
  children,
  disabled,
}: {
  children?: ReactNode;
  disabled?: boolean;
}) {
  const { mixerReady } = useSnapshot(animationPlayerStore.state);
  const {
    cameraSettings: { aspectRatio },
    backgroundSettings: { showGrid, showGroundPlane, gridCellSize = 1 },
  } = useSnapshot(sceneSettingsStore.state);
  const [canvasStyle, setCanvasStyle] = useState<CSSProperties>({
    borderRadius: 'inherit',
  });

  const isNonProductionEnvironment = isNonProduction();

  usePlayerHotkeys(
    'main',
    animationPlayerStore.defaultInstance,
    sceneSettingsStore,
  );

  useEffect(() => {
    const styles: CSSProperties = { ...canvasStyle };

    // For use when we want to override the canvas size.
    // particularly useful for rendering mp4 videos at a specific resolution
    const params = new URLSearchParams(window.location.search);
    if (params.has('canvasWidth') && params.has('canvasHeight')) {
      styles.width = parseInt(params.get('canvasWidth') ?? '1280');
      styles.height = parseInt(params.get('canvasHeight') ?? '960');
    }
    setCanvasStyle(styles);
  }, []);

  useCanvasResizer(aspectRatio);

  return (
    <>
      <div
        id="threejs-div"
        className={`transitiom-colors relative flex h-full w-full flex-none items-center justify-center overflow-hidden rounded-3xl ${
          aspectRatio === -1 ? '' : 'bg-gray-75 dark:bg-gray-60'
        }`}
      >
        {disabled && <div className="bg-gray-90/50 absolute inset-0 z-10" />}
        <ErrorBoundary fallback={<CanvasError className="pb-16" />}>
          <Canvas
            id="r3f-canvas-holder"
            gl={{ preserveDrawingBuffer: true }}
            style={canvasStyle}
            shadows={{
              enabled: true,
              autoUpdate: true,
              type: PCFSoftShadowMap,
            }}
          >
            <SceneSetup
              animationPlayerStore={animationPlayerStore.defaultInstance}
            />
            {children}

            <StageFromBVH />
            {showGrid && (
              <Grid cellSize={gridCellSize} sectionSize={gridCellSize * 5} />
            )}
            {isNonProductionEnvironment && showGroundPlane && <GroundPlane />}
          </Canvas>
        </ErrorBoundary>
      </div>
      {mixerReady && (
        <div className="hidden" id="download-video-can-generate" />
      )}
      <Loader />
    </>
  );
}

export function Loader() {
  const { jobData, currentAnimationNumber } = useSnapshot(
    animationGenerationStore.state,
  );

  const currentVariation = jobData?.variationInfoByID[currentAnimationNumber];
  const status = currentVariation?.status;

  const startedAt =
    jobData && (status === 'GENERATING_BVH' || status === 'GENERATING_PK')
      ? new Date(jobData.lastGenerationRequestAt).getTime()
      : new Date().getTime();
  const progressPercentage = useProgressEstimate(
    startedAt,
    jobData?.estimatedProcessingTime || GENERATION_ESTIMATED_SECONDS,
  );

  if (!status || status === 'COMPLETED') return null;

  const text =
    status === 'QUEUED'
      ? 'Your animation is queued. Please wait...'
      : status === 'GENERATING_BVH' || status === 'GENERATING_PK'
        ? `Processing animation... ${progressPercentage}%`
        : status === 'FAILED'
          ? 'Your animation failed. Please try again.'
          : '';

  return (
    <PlayerDialog maxWidth={240}>
      <div className="flex flex-col items-center">
        {status === 'FAILED' ? (
          <>
            <WarningIcon className="text-red h-10 w-10" />
          </>
        ) : (
          <svg
            width="40"
            height="40"
            className="text-green animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              className="stroke-gray-30 dark:stroke-gray-60"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}

        <Text variant="studio/body/sm" className="mt-6">
          {text}
        </Text>
      </div>
    </PlayerDialog>
  );
}
